import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { i18n } from '@wms/shared';
import { panelPosition, type PanelPlacement } from '../lib/hoverPreview.js';
import { formatLocationCoords, formatQty } from '../lib/format.js';
const { t } = i18n;
export interface StockPalletLocation {
  palletId: string;
  palletCode: string;
  qty: number;
  locationRow: string | null;
  locationShelf: string | null;
  locationPosition: string | null;
}

/** Interactive hover/focus/tap preview outside table overflow clipping. */
export function StockPalletCell({
  value,
  rows = [],
  canOpenPallet = true,
}: {
  value: number;
  rows?: StockPalletLocation[];
  canOpenPallet?: boolean;
}) {
  const [pos, setPos] = useState<PanelPlacement | null>(null);
  const button = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pointer = useRef(false);
  const id = useId();
  const cancel = useCallback(() => {
    if (timer.current !== null) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);
  const close = useCallback(() => {
    cancel();
    setPos(null);
  }, [cancel]);
  const open = () => {
    cancel();
    const rect = button.current?.getBoundingClientRect();
    if (rect)
      setPos(
        panelPosition(
          rect,
          { width: window.innerWidth, height: window.innerHeight },
          { width: 340, height: 280 },
        ),
      );
  };
  const schedule = () => {
    cancel();
    timer.current = setTimeout(close, 150);
  };
  useEffect(() => cancel, [cancel]);
  useEffect(() => {
    if (!pos) return;
    const outside = (event: Event) => {
      const target = event.target as Node | null;
      if (!button.current?.contains(target) && !panel.current?.contains(target)) close();
    };
    const key = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };
    document.addEventListener('pointerdown', outside);
    document.addEventListener('keydown', key);
    window.addEventListener('resize', close);
    window.addEventListener('scroll', close);
    return () => {
      document.removeEventListener('pointerdown', outside);
      document.removeEventListener('keydown', key);
      window.removeEventListener('resize', close);
      window.removeEventListener('scroll', close);
    };
  }, [pos, close]);
  const outsideQty = Math.max(0, value - rows.reduce((sum, row) => sum + row.qty, 0));
  return (
    <>
      <button
        ref={button}
        type="button"
        aria-expanded={Boolean(pos)}
        aria-controls={pos ? id : undefined}
        aria-label={`${t('stock_dash.pallets.title')}: ${formatQty(value)}`}
        onPointerEnter={(event) => {
          if (event.pointerType === 'mouse') open();
        }}
        onPointerLeave={(event) => {
          if (event.pointerType === 'mouse') schedule();
        }}
        onPointerDown={() => {
          pointer.current = true;
        }}
        onFocus={() => {
          if (!pointer.current) open();
        }}
        onBlur={(event) => {
          pointer.current = false;
          if (!panel.current?.contains(event.relatedTarget)) close();
        }}
        onClick={() => {
          pointer.current = false;
          if (pos) close();
          else open();
        }}
        className="underline decoration-dotted underline-offset-2 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      >
        {formatQty(value)}
      </button>
      {pos
        ? createPortal(
            <div
              ref={panel}
              id={id}
              role="region"
              aria-label={t('stock_dash.pallets.title')}
              onPointerEnter={cancel}
              onPointerLeave={schedule}
              onBlur={(event) => {
                if (
                  !panel.current?.contains(event.relatedTarget) &&
                  !button.current?.contains(event.relatedTarget)
                )
                  close();
              }}
              style={{
                position: 'fixed',
                top: pos.top,
                left: pos.left,
                width: pos.width,
                maxHeight: pos.height,
              }}
              className="z-50 overflow-y-auto overscroll-contain rounded-md border border-border bg-background p-3 text-left shadow-lg text-sm"
            >
              <h3 className="mb-2 text-xs font-medium text-muted-foreground">
                {t('stock_dash.pallets.title')}
              </h3>
              {rows.length ? (
                <ul className="space-y-2">
                  {rows.map((row) => (
                    <li key={row.palletId} className="flex justify-between gap-3">
                      <div className="min-w-0">
                        {canOpenPallet ? (
                          <Link
                            to={`/pallets/${row.palletId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-mono text-xs break-all hover:underline"
                          >
                            {row.palletCode}
                          </Link>
                        ) : (
                          <span className="font-mono text-xs break-all">{row.palletCode}</span>
                        )}
                        <div className="text-xs text-muted-foreground">
                          {formatLocationCoords(row)}
                        </div>
                      </div>
                      <span className="shrink-0 tabular-nums">{formatQty(row.qty)}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p>
                  {outsideQty > 0
                    ? t('stock_dash.pallets.shelf_only')
                    : t('stock_dash.pallets.empty')}
                </p>
              )}
              {outsideQty > 0 ? (
                <p className="mt-2 border-t border-border pt-2 text-xs text-muted-foreground">
                  {t('stock_dash.pallets.unallocated')}: {formatQty(outsideQty)}
                </p>
              ) : null}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
